import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Button,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  Filter,
  List,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceManyField,
  required,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  ReferenceInput,
  ImageField,
  useNotify,
  useRedirect,
  useRefresh,
} from 'react-admin';
import { Link } from 'react-router-dom';
import DeleteIcon from '@material-ui/icons/Delete';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { ADMIN_URL } from '../../common/http';

// Custom delete button that calls DELETE /:id/full endpoint
const DeleteFullButton = ({ record }: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this challenge and all related data?')) {
      return;
    }

    try {
      const response = await fetch(`${ADMIN_URL}/challenge/${record.id}/full`, {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
      });

      if (response.ok) {
        notify('Challenge deleted successfully', 'success');
        redirect('/challenge');
        refresh();
      } else {
        notify('Error deleting challenge', 'error');
      }
    } catch (error) {
      notify('Error deleting challenge', 'error');
    }
  };

  return (
    <Button label="Delete" onClick={handleDelete}>
      <DeleteIcon />
    </Button>
  );
};

// Custom bulk delete button that calls DELETE /:id/full for each selected item
const BulkDeleteFullButton = ({ selectedIds }: any) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const unselectAll = useRefresh(); // Will trigger re-render which clears selection

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} challenge(s) and all related data?`)) {
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedIds) {
      try {
        const response = await fetch(`${ADMIN_URL}/challenge/${id}/full`, {
          method: 'DELETE',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
          },
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    if (successCount > 0) {
      notify(`${successCount} challenge(s) deleted successfully`, 'success');
    }
    if (errorCount > 0) {
      notify(`${errorCount} challenge(s) failed to delete`, 'error');
    }

    refresh();
  };

  return (
    <Button label="Delete" onClick={handleBulkDelete}>
      <DeleteIcon />
    </Button>
  );
};

// Link to room show page
const RoomLinkField = ({ record }: any) => {
  if (!record || !record.roomId) {
    return null;
  }
  return (
    <Button component={Link} to={`/room/${record.roomId}/show`} label={record.room?.event_title || `Room ${record.roomId}`}>
      <OpenInNewIcon />
    </Button>
  );
};

const ChallengeFilter = (props: any) => (
  <Filter {...props}>
    <NumberInput label="Race ID" source="roomId||$eq" alwaysOn />
    <TextInput label="Title" source="title||$contL" alwaysOn />
    <TextInput label="Mode" source="mode||$eq" />
    <BooleanInput label="Completed" source="completed||$eq" />
    <NumberInput label="Challenge ID" source="id||$eq" />
  </Filter>
);

const ChallengeBulkActionButtons = (props: any) => (
  <BulkDeleteFullButton {...props} />
);

export const ChallengeList = (props: any) => (
  <List {...props} filters={<ChallengeFilter />} bulkActionButtons={<ChallengeBulkActionButtons />}>
    <Datagrid rowClick="show">
      <TextField source="id" />
      <TextField source="room.event_title" label="Race" />
      <NumberField source="roomId" label="Race ID" />
      <TextField source="title" />
      <TextField source="subTitle" />
      <TextField source="mode" />
      <ImageField source="image.url" label="Image" />
      <NumberField source="distance" />
      <NumberField source="duration" />
      <DateField source="start" />
      <BooleanField source="completed" />
      <DateField source="createdAt" />
      <ShowButton />
      <DeleteFullButton />
    </Datagrid>
  </List>
);

const CommonFormPart = (props: any) => (
  <span>
    <ReferenceInput perPage={200} sort={{ field: 'id', order: 'DESC' }} label="Race" source="roomId" reference="room" fullWidth>
      <SelectInput optionText={(record: any) => record.id + ' | ' + record.event_title + ' | ' + record.startDate} fullWidth />
    </ReferenceInput>
    <TextInput source="title" fullWidth validate={[required()]} />
    <TextInput source="subTitle" fullWidth />
    <TextInput source="mode" fullWidth validate={[required()]} />
    <TextInput source="color" fullWidth />
    <DateTimeInput source="start" fullWidth validate={[required()]} />
    <NumberInput source="duration" fullWidth validate={[required()]} />
    <NumberInput source="distance" fullWidth validate={[required()]} />
    <BooleanInput source="completed" fullWidth />
  </span>
);

export const ChallengeCreate = (props: any) => (
  <Create {...props}>
    <SimpleForm redirect="show">
      <CommonFormPart {...props} />
    </SimpleForm>
  </Create>
);

export const ChallengeEdit = (props: any) => (
  <Edit {...props}>
    <SimpleForm redirect="list">
      <CommonFormPart {...props} />
    </SimpleForm>
  </Edit>
);

export const ChallengeShow = (props: any) => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Challenge">
        <TextField source="id" />
        <RoomLinkField label="Race" />
        <NumberField source="roomId" label="Race ID" />
        <TextField source="title" />
        <TextField source="subTitle" />
        <TextField source="mode" />
        <TextField source="color" />
        <DateField source="start" />
        <NumberField source="duration" />
        <NumberField source="distance" />
        <BooleanField source="completed" />
        <DateField source="createdAt" />
        <DateField source="updatedAt" />
        <DeleteFullButton />
      </Tab>
      <Tab label="Invitations">
        <ReferenceManyField
          pagination={<Pagination />}
          perPage={10}
          label="Challenge Invitations"
          reference="challenge-invitation"
          target="challengeId"
        >
          <Datagrid>
            <TextField source="id" />
            <TextField source="userId" label="User ID" />
            <TextField source="user.username" label="Username" />
            <TextField source="user.email" label="Email" />
            <DateField source="acceptedAt" label="Accepted" />
            <DateField source="declinedAt" label="Declined" />
            <DateField source="silencedAt" label="Silenced" />
            <DateField source="chatMessagesReadAt" label="Chat Read" />
            <DateField source="createdAt" />
            <DateField source="updatedAt" />
          </Datagrid>
        </ReferenceManyField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);
